import type {Channel} from '@appManagers/appChatsManager';
import type {AppSidebarRight} from '@components/sidebarRight';
import type Chat from '@components/chat/chat';
import {RIGHT_COLUMN_ACTIVE_CLASSNAME} from '@components/sidebarRight';
import mediaSizes, {ScreenSize} from '@helpers/mediaSizes';
import rootScope, {BroadcastEvents} from '@lib/rootScope';
import ButtonIcon from '@components/buttonIcon';
import ButtonMenuToggle from '@components/buttonMenuToggle';
import createChatPinnedMessage, {ChatPinnedMessageController} from '@components/chat/pinnedMessage';
import getPinnedMessagesKey from '@appManagers/utils/messages/getPinnedMessagesKey';
import ListenerSetter from '@helpers/listenerSetter';
import showDeleteDialogPopup from '@components/popups/deleteDialog';
import {showPeerReport} from '@components/popups/reportAd';
import appNavigationController from '@components/appNavigationController';
import {LEFT_COLUMN_ACTIVE_CLASSNAME} from '@components/sidebarLeft';
import PeerTitle from '@components/peerTitle';
import I18n, {LangPackKey, i18n} from '@lib/langPack';
import findUpClassName from '@helpers/dom/findUpClassName';
import blurActiveElement from '@helpers/dom/blurActiveElement';
import cancelEvent from '@helpers/dom/cancelEvent';
import {attachClickEvent} from '@helpers/dom/clickEvent';
import {toastNew} from '@components/toast';
import replaceContent from '@helpers/dom/replaceContent';
import {ChatFull, Chat as MTChat, GroupCall, Dialog, InputGroupCall, User, UserFull} from '@layer';
import {showSharingPickerPopup} from '@components/popups/pickUser';
import showPeerPopup, {PopupPeerCheckboxOptions} from '@components/popups/peer';
import {AppEditBotTab, AppEditContactTab} from '@components/solidJsTabs/tabs';
import IS_GROUP_CALL_SUPPORTED from '@environment/groupCallSupport';
import IS_CALL_SUPPORTED from '@environment/callSupport';
import {CallType} from '@lib/calls/types';
import showMutePopup from '@components/popups/mute';
import {AppManagers} from '@lib/managers';
import hasRights from '@appManagers/utils/chats/hasRights';
import wrapPeerTitle from '@components/wrappers/peerTitle';
import groupCallsController from '@lib/calls/groupCallsController';
import apiManagerProxy from '@lib/apiManagerProxy';
import {makeMediaSize} from '@helpers/mediaSize';
import {FOLDER_ID_ALL} from '@appManagers/constants';
import formatNumber from '@helpers/number/formatNumber';
import {modifyAckedPromise} from '@helpers/modifyAckedResult';
import callbackify from '@helpers/callbackify';
import confirmationPopup from '@components/confirmationPopup';
import noop from '@helpers/noop';
import clearHistoryWithConfirmation from '@components/clearHistory';
import canClearHistory from '@appManagers/utils/chats/canClearHistory';
import IS_LIVE_STREAM_SUPPORTED from '@environment/liveStreamSupport';
import {avatarNew, findUpAvatar} from '@components/avatarNew';
import {Middleware, MiddlewareHelper, getMiddleware} from '@helpers/middleware';
import setBadgeContent from '@helpers/setBadgeContent';
import createBadge from '@helpers/createBadge';
import AppStatisticsTab from '@components/sidebarRight/tabs/statistics';
import {ChatType} from './chatType';
import {showRtmpStartStreamPopup} from '@components/rtmp/adminPopup';
import showSendGiftPopup from '@components/popups/sendGift';
import PaidMessagesInterceptor, {PAYMENT_REJECTED} from '@components/chat/paidMessagesInterceptor';
import {openRemoveFeePopup} from '@components/chat/removeFee';
import {createTopbarPlates, TopbarPlates} from '@components/chat/topbarPlates';
import pause from '@helpers/schedulers/pause';
import appImManager from '@lib/appImManager';
import getPeerId from '@appManagers/utils/peers/getPeerId';
import namedPromises from '@helpers/namedPromises';
import appDialogsManager from '@lib/appDialogsManager';
import {createEffect, createRoot, on, untrack} from 'solid-js';
import SolidJSHotReloadGuardProvider from '@lib/solidjs/hotReloadGuardProvider';
import {AppAdminRecentActionsTab} from '@components/solidJsTabs/tabs';
import {setAppSettings} from '@stores/appSettings';
import {wrapAsyncClickHandler} from '@helpers/wrapAsyncClickHandler';
import liteMode from '@helpers/liteMode';
import createSubmenuTrigger from '@components/createSubmenuTrigger';
import ButtonMenu, {ButtonMenuItemOptionsVerifiable} from '@components/buttonMenu';
import {getCachedFullPeer} from '@stores/fullPeers';
import Icon from '@components/icon';
import {getDefaultOptions} from '@components/sidebarLeft/tabs/autoDeleteMessages/options';
import {createAutoDeleteIcon} from '@components/autoDeleteIcon';
import showPremiumPopup from '@components/popups/premium';
import showNoForwardsPopup from '@components/popups/noForwards';
import showAddBotToChat from '@components/popups/addBotToChat';
import openBoosts from '@components/openBoosts';
import getAddBotToChatAction from '@appManagers/utils/bots/getAddBotToChatAction';
import canReportBot from '@appManagers/utils/bots/canReportBot';
import joinChat from '@components/chat/joinChat';

type ButtonToVerify = {element?: HTMLElement, verify: () => boolean | Promise<boolean>};

export default class ChatTopbar {
  public container: HTMLDivElement;
  public floatingPlatesWrapper: HTMLDivElement;
  private btnBack: HTMLButtonElement;
  private btnBackBadge: HTMLElement;
  private chatInfo: HTMLDivElement;
  private avatar: ReturnType<typeof avatarNew>;
  private avatarMiddlewareHelper: MiddlewareHelper;
  private title: HTMLDivElement;
  private subtitle: HTMLDivElement;
  private chatUtils: HTMLDivElement;
  private btnCall: HTMLButtonElement;
  private btnGroupCall: HTMLButtonElement;
  private btnGroupCallMenu: HTMLElement;
  private btnSearch: HTMLButtonElement;
  private btnLogFilters: HTMLButtonElement;
  private btnMore: HTMLElement;

  private autoDeleteBtnMenuOptions: ButtonMenuItemOptionsVerifiable;
  /** The chat's own Delete, whose text depends on what the chat is (Delete Group, Leave Channel…). */
  private deleteChatBtnMenuOptions: ButtonMenuItemOptionsVerifiable;

  public plates: TopbarPlates;
  public pinnedMessage: ChatPinnedMessageController;


  public listenerSetter: ListenerSetter;

  private menuButtons: Parameters<typeof ButtonMenuToggle>[0]['buttons'];
  private buttonsToVerify: ButtonToVerify[];
  private chatInfoContainer: HTMLDivElement;
  private person: HTMLDivElement;

  private titleMiddlewareHelper: MiddlewareHelper;
  private status: ReturnType<ChatTopbar['createStatus']>;

  constructor(
    private chat: Chat,
    public appSidebarRight: AppSidebarRight,
    private managers: AppManagers
  ) {
    this.listenerSetter = new ListenerSetter();

    this.menuButtons = [];
    this.buttonsToVerify = [];
  }

  public construct() {
    // this.chat.log.error('Topbar construction');

    this.container = document.createElement('div');
    this.container.classList.add('sidebar-header', 'topbar', 'hide');
    this.container.dataset.floating = '0';

    // Preview mode swaps the back arrow for a close icon — the popup wires this up to its
    // own hide() via `chat.onPreviewClose`, and the folder back-unread badge is irrelevant
    // here (there's no folder navigation behind a floating preview).
    const backIcon = this.chat.isPreview ? 'close' : 'left';
    this.btnBack = ButtonIcon(`${backIcon} sidebar-close-button`, {noRipple: true, ariaLabel: 'Close'});
    if(!this.chat.isPreview) {
      this.btnBackBadge = createBadge('span', 20, 'primary');
      this.btnBackBadge.classList.add('back-unread-badge');
      this.btnBack.append(this.btnBackBadge);
    }

    // * chat info section
    this.chatInfoContainer = document.createElement('div');
    this.chatInfoContainer.classList.add('chat-info-container');

    this.chatInfo = document.createElement('div');
    this.chatInfo.classList.add('chat-info');

    const person = this.person = document.createElement('div');
    person.classList.add('person');

    const content = document.createElement('div');
    content.classList.add('content');

    const top = document.createElement('div');
    top.classList.add('top');

    this.title = document.createElement('div');
    this.title.classList.add('user-title');

    top.append(this.title);

    const bottom = document.createElement('div');
    bottom.classList.add('bottom');

    if(this.subtitle) {
      bottom.append(this.subtitle);
    }

    content.append(top, bottom);

    person.append(content);
    this.chatInfo.append(person);

    // * chat utils section
    this.chatUtils = document.createElement('div');
    this.chatUtils.classList.add('chat-utils');

    this.plates = createTopbarPlates(this, this.chat, this.managers);

    if(this.menuButtons.length) {
      this.btnMore = ButtonMenuToggle({
        buttonOptions: {ariaLabel: 'MultiAccount.More'},
        listenerSetter: this.listenerSetter,
        direction: 'bottom-left',
        buttons: this.menuButtons,
        positionPadding: {top: 7},
        onOpenBefore: async() => {
          const hasAutoDeleteButton = await this.chat.canManageAutoDelete();
          if(!hasAutoDeleteButton) return;

          const period = await this.chat.getAutoDeletePeriod().then(ackedResult => ackedResult.result);
          this.autoDeleteBtnMenuOptions.iconElement = createAutoDeleteIcon(period);
        },
        onOpen: async(e, element) => {
          const deleteButton = this.deleteChatBtnMenuOptions;
          if(deleteButton?.element) {
            const deleteButtonText = await this.managers.appPeersManager.getDeleteButtonText(this.chat.monoforumThreadId || this.peerId);
            deleteButton.element.lastChild.replaceWith(i18n(deleteButtonText));
          }

          this.autoDeleteBtnMenuOptions.onOpen?.();
        },
        onClose: () => {
          this.autoDeleteBtnMenuOptions.onClose?.();
        }
      });
    }

    this.chatUtils.append(...[
      // this.chatAudio ? this.chatAudio.divAndCaption.container : null,
      // this.pinnedMessage ? this.pinnedMessage.pinnedMessageContainer.divAndCaption.container : null,
      // btnJoin / btnMute / btnDirectMessages moved into the chat-input control plate (see input.ts).
      this.btnCall,
      this.btnGroupCall,
      this.btnGroupCallMenu,
      this.btnSearch,
      this.btnLogFilters,
      this.btnMore
    ].filter(Boolean));

    this.pushButtonToVerify(this.btnCall, this.verifyCallButton.bind(this, 'voice'));
    this.pushButtonToVerify(this.btnGroupCall, this.verifyVideoChatButton.bind(this, 'nonadmin'));
    this.pushButtonToVerify(this.btnGroupCallMenu, this.verifyRtmpButton.bind(this));

    // Preview keeps only the basic header — avatar + title + subtitle. The chat-utils
    // pane (search / menu buttons) and the floating-plates wrapper (call / pinned /
    // translation / action bar) are built so the rest of the JS can call methods on
    // them, but never attached to the live DOM tree. They sit detached, so nothing
    // visible leaks into the popup.
    this.chatInfoContainer.append(this.btnBack, this.chatInfo);
    if(!this.chat.isPreview) this.chatInfoContainer.append(this.chatUtils);
    this.container.append(this.chatInfoContainer);

    this.floatingPlatesWrapper = document.createElement('div');
    this.floatingPlatesWrapper.classList.add('topbar-floating-plates', 'hide');
    if(!this.chat.isPreview) this.container.append(this.floatingPlatesWrapper);

    // the pinned plate is prepended here by `revealPreparedPinnedMessage`, in the
    // same frame as the bubbles it belongs to

    this.plates.mount(this.floatingPlatesWrapper);

    // * construction end

    // * fix topbar overflow section

    this.listenerSetter.add(window)('resize', this.onResize);
    this.listenerSetter.add(mediaSizes)('changeScreen', this.onChangeScreen);

    attachClickEvent(this.container, (e) => {
      if(
        findUpClassName(e.target, 'topbar-search-container') ||
        !(e.target as HTMLElement).isConnected ||
        findUpClassName(e.target, 'chat-search-top')
      ) {
        return;
      }

      const container = findUpClassName(e.target, 'pinned-container');
      blurActiveElement();
      if(container) {
        return;
      } else {
        const avatar = findUpAvatar(e.target);
        if(mediaSizes.activeScreen === ScreenSize.medium && document.body.classList.contains(LEFT_COLUMN_ACTIVE_CLASSNAME)) {
          onBtnBackClick();
        } else if(avatar) {
          if(avatar.classList.contains('has-stories')) {
            return;
          }

          this.appSidebarRight.toggleSidebar(!document.body.classList.contains(RIGHT_COLUMN_ACTIVE_CLASSNAME));
        } else {
          this.appSidebarRight.toggleSidebar(true);
        }
      }
    }, {listenerSetter: this.listenerSetter});

    const onBtnBackClick = (e?: Event) => {
      if(e) {
        cancelEvent(e);
      }

      if(this.chat.isPreview) {
        this.chat.onPreviewClose?.();
        return;
      }

      if(this.chat.type === ChatType.Search) {
        this.chat.resetSearch();
        return;
      }

      this.chat.pop();
    };

    attachClickEvent(this.btnBack, onBtnBackClick, {listenerSetter: this.listenerSetter});
  }

  private pushButtonToVerify(element: HTMLElement, verify: ButtonToVerify['verify']) {
    if(!element) {
      return;
    }

    element.classList.add('hide');
    this.buttonsToVerify.push({element, verify});
  }

  private verifyButtons = (e?: Event) => {
    const isMenuOpen = !!e || !!(this.btnMore && this.btnMore.classList.contains('menu-open'));

    e && cancelEvent(e);

    const r = async() => {
      const buttons = this.buttonsToVerify.concat(isMenuOpen ? this.menuButtons as any : []);
      const results = await Promise.all(buttons.map(async(button) => {
        return {
          result: await button.verify(),
          button
        }
      }));

      results.forEach(({button, result}) => {
        button.element?.classList.toggle('hide', !result);
      });
    };

    r();
  };

  private verifyRtmpButton = async() => {
    if(!this.chat.isBroadcast || this.chat.type !== ChatType.Chat) {
      return false;
    }

    const currentGroupCall = groupCallsController.groupCall;
    const chatId = this.peerId.toChatId();
    if(currentGroupCall?.chatId === chatId) {
      return false;
    }

    const chat = this.chat.peer as MTChat.channel | MTChat.chat;
    if(!chat) {
      return false;
    }

    const isCallActive = !!(chat as MTChat.chat).pFlags?.call_active;
    if(isCallActive) {
      return false;
    }

    return hasRights(chat, 'manage_call');
  };

  private verifyVideoChatButton = async(type?: 'group' | 'broadcast' | 'nonadmin') => {
    if(
      !IS_GROUP_CALL_SUPPORTED ||
      this.peerId.isUser() ||
      this.chat.type !== ChatType.Chat ||
      this.chat.threadId
    ) return false;

    const currentGroupCall = groupCallsController.groupCall;
    const chatId = this.peerId.toChatId();
    if(currentGroupCall?.chatId === chatId) {
      return false;
    }

    if(type) {
      if(((type === 'group' && !this.chat.isAnyGroup)) ||
        ((type === 'broadcast' && !this.chat.isBroadcast))) {
        return false;
      }
    }

    const chat = this.chat.peer as MTChat.channel | MTChat.chat;
    const isCallActive = !!(chat as MTChat.chat).pFlags?.call_active;
    const canManageCall = hasRights(chat, 'manage_call');
    if(type === 'nonadmin' && canManageCall && this.chat.isBroadcast) {
      return false; // * hide live stream top button
    }
    const needActiveCall = !canManageCall;
    if(!isCallActive) {
      return !needActiveCall;
    }

    const fullChat = await this.managers.appProfileManager.getChatFull(chatId);
    if(!('call' in fullChat) || !fullChat.call) {
      return false;
    }
    const inputGroupCall = fullChat.call as InputGroupCall.inputGroupCall;
    const groupCall = await this.managers.appGroupCallsManager.getGroupCallFull(
      inputGroupCall.id
    );
    if(groupCall?._ !== 'groupCall') return false;

    return !groupCall.pFlags.rtmp_stream;
  };

  private verifyCallButton = async(type?: CallType) => {
    if(!IS_CALL_SUPPORTED || !this.peerId.isUser() || this.chat.type !== ChatType.Chat) return false;
    const userId = this.peerId.toUserId();
    const userFull = await this.managers.appProfileManager.getCachedFullUser(userId);

    return !!userFull && !!(type === 'voice' ? userFull.pFlags.phone_calls_available : userFull.pFlags.video_calls_available);
  };

  private verifyIfCanReportChat = () => {
    if(
      this.chat.type !== ChatType.Chat ||
      this.chat.threadId ||
      this.chat.isMonoforum
    ) {
      return false;
    }

    const peer = this.chat.peer;
    if(peer?._ === 'user') {
      return canReportBot(this.peerId, peer);
    }

    return !!peer && (peer._ === 'chat' || peer._ === 'channel') && !peer.pFlags.creator;
  };

  private verifyIfCanClearHistory = async() => {
    if(
      this.chat.type !== ChatType.Chat ||
      this.chat.threadId ||
      this.chat.monoforumThreadId ||
      this.chat.isMonoforum
    ) {
      return false;
    }

    return canClearHistory(this.chat.peer) &&
      !!(await this.managers.appMessagesManager.getDialogOnly(this.peerId));
  }

  private verifyIfCanDeleteChat = async() => {
    if(this.chat.isMonoforum) {
      const chat = this.chat.peer;
      return chat?._ === 'channel' && !chat?.pFlags?.creator && !chat?.pFlags?.left;
    }

    if(this.chat.type === ChatType.Saved) return true;

    return (
      this.chat.type === ChatType.Chat &&
      !!(await this.managers.appMessagesManager.getDialogOnly(this.peerId))
    );
  }

  public constructUtils() {
    this.autoDeleteBtnMenuOptions = createSubmenuTrigger({
      options: {
        text: 'AutoDeleteMessagesShort',
        separatorDown: true,
        verify: this.chat.canManageAutoDelete
      },
      createSubmenu: this.createAutoDeleteSubmenu.bind(this),
      direction: 'left-start'
    });

    const onBoostClick = () => openBoosts({peerId: this.peerId, slider: this.appSidebarRight});

    const getBotAddToChatAction = () => this.peerId.isUser() && getAddBotToChatAction(
      this.chat.peer as User.user,
      this.chat.fullPeer() as UserFull.userFull
    );
    const onAddBotToChat = () => showAddBotToChat({botId: this.peerId.toUserId()});
    const verifyAddBotToChat = (text: LangPackKey) => () => getBotAddToChatAction()?.text === text;

    this.menuButtons = [this.autoDeleteBtnMenuOptions, {
      icon: 'search',
      text: 'Search',
      onClick: () => {
        this.chat.initSearch();
      },
      verify: () => mediaSizes.isMobile
    }, {
      icon: 'filter',
      text: 'FilterActions',
      onClick: () => {
        this.onFilterActionsClick();
      },
      verify: () => mediaSizes.isMobile && this.chat.type === ChatType.Logs
    }, /* {
      icon: 'pinlist',
      text: 'Pinned Messages',
      onClick: () => this.openPinned(false),
      verify: () => mediaSizes.isMobile
    }, */{
      icon: 'mute',
      text: 'ChatList.Context.Mute',
      onClick: this.onMuteClick,
      verify: async() => this.chat.type === ChatType.Chat && !this.chat.monoforumThreadId && rootScope.myId !== this.peerId && !(await this.managers.appNotificationsManager.isPeerLocalMuted({peerId: this.peerId, respectType: false, threadId: this.chat.threadId}))
    }, {
      icon: 'unmute',
      text: 'ChatList.Context.Unmute',
      onClick: this.onUnmuteClick,
      verify: () => this.chat.type === ChatType.Chat && !this.chat.monoforumThreadId && rootScope.myId !== this.peerId && this.managers.appNotificationsManager.isPeerLocalMuted({peerId: this.peerId, respectType: false, threadId: this.chat.threadId})
    }, {
      icon: 'comments',
      text: 'ViewDiscussion',
      onClick: () => {
        const middleware = this.chat.bubbles.getMiddleware();
        Promise.resolve(this.managers.appProfileManager.getChannelFull(this.peerId.toChatId())).then((channelFull) => {
          if(middleware() && channelFull.linked_chat_id) {
            this.chat.appImManager.setInnerPeer({
              peerId: channelFull.linked_chat_id.toPeerId(true)
            });
          }
        });
      },
      verify: async() => {
        const chatFull = await this.managers.appProfileManager.getCachedFullChat(this.peerId.toChatId());
        return this.chat.type === ChatType.Chat && !!(chatFull as ChatFull.channelFull)?.linked_chat_id;
      }
    }, {
      icon: 'phone',
      text: 'Call',
      onClick: this.onCallClick.bind(this, 'voice'),
      verify: this.verifyCallButton.bind(this, 'voice')
    }, {
      icon: 'videocamera',
      text: 'VideoCall',
      onClick: this.onCallClick.bind(this, 'video'),
      verify: this.verifyCallButton.bind(this, 'video')
    }, {
      icon: 'videochat',
      text: 'PeerInfo.Action.LiveStream',
      onClick: this.onJoinGroupCallClick,
      verify: this.verifyVideoChatButton.bind(this, 'broadcast')
    }, {
      icon: 'videochat',
      text: 'PeerInfo.Action.VoiceChat',
      onClick: this.onJoinGroupCallClick,
      verify: this.verifyVideoChatButton.bind(this, 'group')
    }, {
      icon: 'topics',
      text: 'TopicViewAsTopics',
      onClick: () => {
        this.chat.appImManager.toggleViewAsMessages(this.peerId, false);
      },
      verify: async() => {
        // the flag outlives the forum it was set for, so ask the peer too
        if(!apiManagerProxy.isForum(this.peerId)) {
          return false;
        }

        const dialog = await this.managers.appMessagesManager.getDialogOnly(this.peerId);
        return !!(dialog && (dialog as Dialog.dialog).pFlags.view_forum_as_messages);
      }
    }, {
      icon: 'topics',
      text: 'SavedViewAsChats',
      onClick: () => {
        this.chat.appImManager.toggleViewAsMessages(this.peerId, false);
      },
      verify: () => this.peerId === rootScope.myId && !this.chat.threadId && !this.chat.appSettings.savedAsForum
    }, {
      icon: 'message',
      text: 'ChannelDirectMessages.ViewChats',
      onClick: () => {
        appDialogsManager.toggleForumTabByPeerId(this.peerId, true);
      },
      verify: () => this.chat.isMonoforum && !this.chat.monoforumThreadId && this.chat.canManageDirectMessages && !mediaSizes.isLessThanFloatingLeftSidebar && !appDialogsManager.hasForumOpenFor(this.peerId)
    }, {
      icon: 'select',
      text: 'Chat.Menu.SelectMessages',
      onClick: () => {
        const selection = this.chat.selection;
        selection.toggleSelection(true, true);
        if(this.chat.appSettings.chatContextMenuHintWasShown) {
          return;
        }

        const original = selection.toggleByElement.bind(selection);
        selection.toggleByElement = async(bubble) => {
          this.chat.setAppSettings('chatContextMenuHintWasShown', true);
          toastNew({langPackKey: 'Chat.Menu.Hint'});

          selection.toggleByElement = original;
          selection.toggleByElement(bubble);
        };
      },
      verify: () => !this.chat.selection.isSelecting && !!this.chat.bubbles.getRenderedLength() && !(this.chat.type === ChatType.Logs)
    }, {
      icon: 'select',
      text: 'Chat.Menu.ClearSelection',
      onClick: () => {
        this.chat.selection.cancelSelection();
      },
      verify: () => this.chat.selection.isSelecting
    }, {
      icon: 'adduser',
      text: 'AddContact',
      onClick: () => {
        this.addContact();
      },
      verify: async() => !this.chat.isBot && (this.chat.monoforumThreadId || this.peerId).isUser() && !(await this.managers.appPeersManager.isContact(this.chat.monoforumThreadId || this.peerId))
    }, {
      icon: 'adduser',
      text: 'AddToGroup',
      onClick: onAddBotToChat,
      verify: verifyAddBotToChat('AddToGroup')
    }, {
      icon: 'adduser',
      text: 'BotAddToGroupOrChannel',
      onClick: onAddBotToChat,
      verify: verifyAddBotToChat('BotAddToGroupOrChannel')
    }, {
      icon: 'adduser',
      text: 'AddToChannel',
      onClick: onAddBotToChat,
      verify: verifyAddBotToChat('AddToChannel')
    }, {
      icon: 'forward',
      text: 'ShareContact',
      onClick: () => {
        const contactPeerId = this.peerId;
        showSharingPickerPopup({
          onSelect: async(chosen) => {
            const {peerId, monoforumThreadId} = chosen[0];
            const preparedPaymentResult = await PaidMessagesInterceptor.prepareStarsForPayment({messageCount: 1, peerId});
            if(preparedPaymentResult === PAYMENT_REJECTED) throw new Error();

            const send = () => {
              this.managers.appMessagesManager.sendContact({peerId, contactPeerId, confirmedPaymentResult: preparedPaymentResult, monoforumThreadId});
              this.chat.appImManager.setInnerPeer({peerId});
            };

            if(preparedPaymentResult) return void send();

            return new Promise((resolve, reject) => {
              showPeerPopup('', {
                titleLangKey: 'SendMessageTitle',
                descriptionLangKey: 'SendContactToGroupText',
                descriptionLangArgs: [new PeerTitle({peerId, dialog: true}).element],
                buttons: [{
                  langKey: 'Send',
                  callback: async() => {
                    resolve();
                    send();
                  }
                }, {
                  langKey: 'Cancel',
                  callback: () => {
                    reject();
                  },
                  isCancel: true
                }],
                peerId
              });
            });
          }
        });
      },
      verify: async() => rootScope.myId !== this.peerId && this.peerId.isUser() && (await this.managers.appPeersManager.isContact(this.peerId)) && !!(await this.managers.appUsersManager.getUser(this.peerId.toUserId())).phone
    }, {
      icon: 'gift',
      text: 'Chat.Menu.SendGift',
      onClick: () => showSendGiftPopup({peerId: this.peerId}),
      verify: async() => (
        this.chat.isChannel || (this.chat.peerId.isUser() && !this.chat.isBot && (await this.managers.appUsersManager.isRegularUser(this.peerId)))
      ) && !(this.chat.type === ChatType.Logs)
    }, {
      icon: 'message',
      text: 'ChannelDirectMessages.Manage',
      onClick: () => this.onDirectMessagesClick(),
      verify: () => this.chat.isChannel && this.chat.canManageDirectMessages && !this.chat.isMonoforum && !!(this.chat.peer as MTChat.channel).linked_monoforum_id && this.chat.type !== ChatType.Logs
    }, {
      icon: 'statistics_chart',
      text: 'Statistics',
      onClick: () => {
        this.appSidebarRight.createTab(AppStatisticsTab).open(this.peerId.toChatId());
        this.appSidebarRight.toggleSidebar(true);
      },
      verify: () => !this.chat.monoforumThreadId && this.managers.appProfileManager.canViewStatistics(this.peerId)
    }, {
      icon: 'addboost',
      text: 'BoostChannel',
      onClick: onBoostClick,
      verify: async() => this.chat.isBroadcast
    }, {
      icon: 'addboost',
      text: 'BoostGroup',
      onClick: onBoostClick,
      verify: async() => this.chat.isMegagroup
    }, {
      icon: 'bots',
      text: 'Settings',
      onClick: () => {
        // [ ] Bot with paid stars?
        this.managers.appMessagesManager.sendText({peerId: this.peerId, text: '/settings'});
      },
      verify: async() => {
        try {
          const attachMenuBot = await this.managers.appAttachMenuBotsManager.getAttachMenuBot(this.peerId.toUserId());
          return !!attachMenuBot?.pFlags?.has_settings;
        } catch(err) {
          return false;
        }
      }
    }, {
      icon: 'premium_translate',
      text: 'Translate',
      onClick: () => {
        this.managers.appTranslationsManager.togglePeerTranslations(
          this.peerId,
          false
        ).then(() => {
          if(this.chat.peerTranslation.canTranslate()) {
            this.chat.peerTranslation.toggle(true);
          }
        });
      },
      verify: async() => {
        if(!this.chat.peerTranslation.areTranslationsAvailable()) {
          return false;
        }

        const fullPeer = await this.managers.appProfileManager.getCachedProfileByPeerId(this.peerId);
        return fullPeer && 'pFlags' in fullPeer && !!fullPeer.pFlags.translations_disabled;
      }
    }, {
      icon: 'lock',
      text: 'BlockUser',
      onClick: () => {
        this.blockUser();
      },
      verify: async() => {
        if(!this.peerId.isUser()) return false;
        const userFull = await this.managers.appProfileManager.getCachedFullUser(this.peerId.toUserId());
        return this.peerId !== rootScope.myId && userFull && !userFull.pFlags?.blocked;
      }
    }, {
      icon: 'lockoff',
      text: 'Unblock',
      onClick: () => {
        this.managers.appUsersManager.toggleBlock(this.peerId, false).then(() => {
          toastNew({langPackKey: 'UserUnblocked'});
        });
      },
      verify: async() => {
        const userFull = await this.managers.appProfileManager.getCachedFullUser(this.peerId.toUserId());
        return !!userFull?.pFlags?.blocked;
      }
    }, {
      icon: 'sharingoff',
      text: 'DisableSharing',
      onClick: () => {
        if(!rootScope.premium) {
          showPremiumPopup({feature: 'pm_noforwards'});
          return;
        }

        const cb = () => this.managers.appProfileManager.toggleNoForwards(this.peerId, true);
        if(this.chat.appSettings.seenTooltips.noForwards) {
          cb();
        } else {
          showNoForwardsPopup(() => {
            setAppSettings('seenTooltips', 'noForwards', true);
            cb();
          });
        }
      },
      verify: async() => {
        if(!this.peerId.isUser() || this.peerId === rootScope.myId || this.chat.isBot) return false;
        const userFull = this.chat.fullPeer() as UserFull.userFull;
        if(!userFull) return false;
        return !userFull.pFlags.noforwards_my_enabled && !userFull.pFlags.noforwards_peer_enabled;
      }
    }, {
      icon: 'sharingon',
      text: 'EnableSharing',
      onClick: async() => {
        const userFull = this.chat.fullPeer() as UserFull.userFull;
        const {peerId} = this;

        if(userFull.pFlags.noforwards_peer_enabled) {
          await confirmationPopup({
            titleLangKey: 'EnableSharing',
            descriptionLangKey: 'EnableSharingCaption',
            descriptionLangArgs: [new PeerTitle({peerId}).element],
            button: {
              langKey: 'SendRequest'
            }
          });
        }

        this.managers.appProfileManager.toggleNoForwards(peerId, false);
      },
      verify: async() => {
        if(!this.peerId.isUser() || this.peerId === rootScope.myId || this.chat.isBot) return false;
        const userFull = this.chat.fullPeer() as UserFull.userFull;
        if(!userFull) return false;
        return !!(userFull?.pFlags?.noforwards_my_enabled || userFull?.pFlags?.noforwards_peer_enabled);
      }
    }, {
      icon: 'dollar_circle',
      text: 'PaidMessages.ChargeFee',
      onClick: () => this.onToggleFeeClick(true),
      verify: () => this.verifyToggleFee(true)
    }, {
      icon: 'dollar_circle_x',
      text: 'PaidMessages.RemoveFee',
      onClick: () => this.onToggleFeeClick(false),
      verify: () => this.verifyToggleFee(false)
    }, {
      icon: 'clipboard',
      text: 'CompactDiffView',
      onClick: wrapAsyncClickHandler(async() => {
        if(!this.chat.popIfMoreThanOne()) {
          await this.chat.setPeer({peerId: this.peerId});
        }

        setAppSettings('logsDiffView', true);

        // awaiting this doesn't help
        this.appSidebarRight.toggleSidebar(true, true);

        if(liteMode.isAvailable('animations')) await pause(100);

        this.appSidebarRight.createTab(AppAdminRecentActionsTab).open({
          channelId: this.peerId.toChatId(),
          isBroadcast: this.chat.isBroadcast
        });
      }),
      verify: () => this.chat.type === ChatType.Logs
    }, {
      icon: 'flag',
      text: 'ReportChat',
      onClick: () => {
        showPeerReport(this.peerId);
      },
      verify: this.verifyIfCanReportChat
    }, {
      icon: 'message_crossed',
      text: 'ClearHistory',
      onClick: () => {
        clearHistoryWithConfirmation({peerId: this.peerId, managers: this.managers});
      },
      verify: this.verifyIfCanClearHistory
    }, {
      icon: 'delete',
      danger: true,
      text: 'Delete',
      onClick: () => {
        showDeleteDialogPopup(
          this.chat.monoforumThreadId || this.peerId,
          undefined,
          undefined,
          this.chat.threadId,
          this.chat.monoforumThreadId ? this.peerId : undefined
        );
      },
      verify: this.verifyIfCanDeleteChat
    }];
    this.deleteChatBtnMenuOptions = this.menuButtons[this.menuButtons.length - 1];

    // the welcome messages section has one thing to offer: clearing them all (desktop's menu)
    this.menuButtons.forEach((button) => {
      const verify = button.verify;
      button.verify = () => this.chat.type !== ChatType.Welcome && (verify ? verify() : true);
    });
    this.menuButtons.push({
      icon: 'delete',
      danger: true,
      text: 'WelcomeMessages.DeleteAll',
      onClick: () => {
        const peerId = this.peerId;
        confirmationPopup({
          descriptionLangKey: 'WelcomeMessages.DeleteAllSure',
          button: {langKey: 'Delete', isDanger: true}
        }).then(() => {
          this.managers.appMessagesManager.deleteAllWelcomeMessages(peerId);
        }, noop);
      },
      verify: async() => this.chat.type === ChatType.Welcome &&
        !!(await this.managers.appMessagesManager.getWelcomeMessagesCount(this.peerId))
    });

    this.btnSearch = ButtonIcon('search', {ariaLabel: 'Search'});
    this.attachClickEvent(this.btnSearch, (e) => {
      this.chat.initSearch();
    }, true);

    this.btnLogFilters = ButtonIcon('filter', {ariaLabel: 'AdminRecentActionsFilters.ByType'});
    this.attachClickEvent(this.btnLogFilters, () => {
      this.onFilterActionsClick();
    });
  }

  public addContact() {
    if(!this.appSidebarRight.isTabExists(AppEditContactTab)) {
      this.appSidebarRight.createTab(AppEditContactTab).open(this.peerId);

      this.appSidebarRight.toggleSidebar(true);
    }
  }

  /** Bot we manage — opens the same tab as the profile's edit button. */
  public editBot() {
    if(!this.appSidebarRight.isTabExists(AppEditBotTab)) {
      this.appSidebarRight.createTab(AppEditBotTab).open(this.peerId);

      this.appSidebarRight.toggleSidebar(true);
    }
  }

  public async blockUser(showReport?: boolean, showDelete?: boolean, onConfirmed?: (promise: Promise<any>) => void) {
    const peerId = this.peerId;
    const checkboxes: PopupPeerCheckboxOptions[] = [
      showReport && {
        text: 'DeleteReportSpam',
        checked: true
      },
      showDelete && {
        text: 'DeleteThisChat',
        checked: true
      }
    ];

    const checked = await confirmationPopup({
      peerId,
      titleLangKey: 'BlockUser',
      descriptionLangKey: 'AreYouSureBlockContact2',
      descriptionLangArgs: [new PeerTitle({peerId}).element],
      button: {
        langKey: 'BlockUser',
        isDanger: true
      },
      checkboxes: checkboxes.filter(Boolean)
    });

    const [reportSpam, deleteChat] = Array.isArray(checked) ? checked : [];

    const promise = Promise.all([
      reportSpam && this.managers.appMessagesManager.reportSpam(peerId),
      deleteChat && this.managers.appMessagesManager.flushHistory({peerId, justClear: false, revoke: true}),
      this.managers.appUsersManager.toggleBlock(peerId, true)
    ]);

    onConfirmed?.(promise);

    await promise;

    toastNew({langPackKey: 'UserBlocked'});
  }

  public attachClickEvent(el: HTMLElement, cb: (e: MouseEvent) => void, noBlur?: boolean) {
    attachClickEvent(el, (e) => {
      cancelEvent(e);
      !noBlur && blurActiveElement();
      cb(e);
    }, {listenerSetter: this.listenerSetter});
  }

  private onCallClick(type: CallType) {
    this.chat.appImManager.callUser(this.peerId.toUserId(), type);
  }

  private verifyToggleFee = async(requirePayment: boolean) => {
    if(!this.chat.monoforumThreadId) return;

    const {chat, dialog} = await namedPromises({
      chat: this.chat.peer,
      dialog: this.managers.monoforumDialogsStorage.getDialogByParent(this.peerId, this.chat.monoforumThreadId)
    });

    if(chat?._ !== 'channel' || !chat?.send_paid_messages_stars) return false;

    return requirePayment ? !!dialog?.pFlags?.nopaid_messages_exception : !dialog?.pFlags?.nopaid_messages_exception;
  };

  private onToggleFeeClick = async(requirePayment: boolean) => {
    const peerId = this.chat.monoforumThreadId;
    const parentPeerId = this.chat.peerId;
    if(!peerId || !parentPeerId) return;

    try {
      await openRemoveFeePopup({peerId, parentPeerId, requirePayment, managers: this.managers})
    } catch{}
  }

  private onJoinGroupCallClick = () => {
    this.chat.appImManager.joinGroupCall(this.peerId);
  };

  private onFilterActionsClick = wrapAsyncClickHandler(async() => {
    const {default: showLogFiltersPopup} = await import('./logFiltersPopup');

    showLogFiltersPopup({
      channelId: this.peerId.toChatId(),
      isBroadcast: this.chat.isBroadcast,
      committedFilters: this.chat.bubbles.committedLogsFilters,
      HotReloadGuard: SolidJSHotReloadGuardProvider,
      onFinish: ({committedFilters}) => {
        this.chat.bubbles.setLogFilters(committedFilters);
      }
    });
  })

  /**
   * The welcome section's menu holds one action, "Delete All": the button is there while there is
   * something to delete. `load` asks the server on opening (shared with the list's own request).
   */
  private async updateWelcomeMoreButton(load?: boolean) {
    const {peerId, btnMore} = this;
    if(this.chat.type !== ChatType.Welcome || !btnMore) return;
    const count = await this.managers.appMessagesManager.getWelcomeMessagesCount(peerId, load);
    if(this.peerId !== peerId || this.chat.type !== ChatType.Welcome) return;
    btnMore.classList.toggle('hide', !count);
  }

  private get peerId() {
    return this.chat.peerId;
  }

  public constructPeerHelpers() {
    this.subtitle = document.createElement('div');
    this.subtitle.classList.add('info');

    // No plate here: `setupPinnedMessageForPeer` builds one per peer/thread and
    // installs it in `revealPreparedPinnedMessage`. Creating one eagerly only
    // produced a throwaway plate (rendered, listened, then destroyed on the
    // first peer change) whose `isStatic` was decided before the chat had a type.

    this.btnCall = ButtonIcon('phone', {ariaLabel: 'Call'});
    this.btnGroupCall = ButtonIcon('videochat', {ariaLabel: 'PeerInfo.Action.VoiceChat'});
    this.btnGroupCallMenu = ButtonMenuToggle({
      buttonOptions: {ariaLabel: 'PeerInfo.Action.VoiceChat'},
      listenerSetter: this.listenerSetter,
      direction: 'bottom-left',
      buttons: [{
        icon: 'videochat',
        text: 'Rtmp.Topbar.StartVideoChat',
        onClick: this.onJoinGroupCallClick
      }, {
        icon: 'link',
        text: 'Rtmp.Topbar.StreamWith',
        onClick: () => {
          showRtmpStartStreamPopup({peerId: this.peerId});
        },
        verify: () => IS_LIVE_STREAM_SUPPORTED
      }],
      icon: 'videochat'
    });
    this.attachClickEvent(this.btnCall, this.onCallClick.bind(this, 'voice'));
    this.attachClickEvent(this.btnGroupCall, this.onJoinGroupCallClick);

    this.listenerSetter.add(rootScope)('welcome_message_new', (message) => {
      if(message.peerId === this.peerId) void this.updateWelcomeMoreButton();
    });

    this.listenerSetter.add(rootScope)('welcome_messages_delete', ({peerId}) => {
      if(peerId === this.peerId) void this.updateWelcomeMoreButton();
    });

    this.listenerSetter.add(rootScope)('folder_unread', (folder) => {
      if(!this.btnBackBadge || folder.id !== FOLDER_ID_ALL) {
        return;
      }

      const size = folder.unreadUnmutedPeerIds.size;
      setBadgeContent(this.btnBackBadge, size ? '' + formatNumber(size, 1) : '');
      // this.btnBack.classList.remove('tgico-left', 'tgico-previous');
      // this.btnBack.classList.add(size ? 'tgico-previous' : 'tgico-left');
    });

    this.listenerSetter.add(rootScope)('chat_update', (chatId) => {
      if(this.peerId === chatId.toPeerId(true)) {
        const chat = this.chat.peer as Channel/*  | Chat */;
        if(!chat.pFlags.broadcast) {
          return;
        }

        this.verifyButtons();
      }
    });

    this.listenerSetter.add(rootScope)('peer_full_update', (peerId) => {
      if(this.peerId === peerId) {
        this.verifyButtons();
      }
    });

    this.listenerSetter.add(rootScope)('chat_requests', ({chatId, recentRequesters, requestsPending}) => {
      if(this.peerId !== chatId.toPeerId(true)) {
        return;
      }

      const middleware = this.chat.bubbles.getMiddleware();
      this.plates.requests.set(
        this.peerId,
        recentRequesters.map((userId) => userId.toPeerId(false)),
        requestsPending
      ).then((callback) => {
        if(!middleware()) {
          return;
        }

        callback();
      });
    });

    this.listenerSetter.add(rootScope)('peer_settings', async({peerId, settings}) => {
      if(this.peerId !== peerId) {
        return;
      }

      const callback = this.plates.actions.set(peerId, settings);
      callback();
    });

    this.listenerSetter.add(rootScope)('chat_automation_update', (connectedBot) => {
      this.plates.automation.handleConnectedBotUpdate(connectedBot?.bot_id as UserId);
    });

    this.listenerSetter.add(rootScope)('right_sidebar_toggle', () => {
      this.setFloating(); // * to calculate sponsored height
    });

    this.chat.addEventListener('setPeer', (mid, isTopMessage) => {
      const middleware = this.chat.bubbles.getMiddleware();
      if(!middleware() || !this.pinnedMessage) return;

      this.pinnedMessage.setUserHidden(!!this.chat.appState.hiddenPinnedMessages[getPinnedMessagesKey(this.chat.peerId, this.chat.threadId)]);

      if(isTopMessage) {
        this.pinnedMessage.unsetScrollDownListener();
        this.pinnedMessage.testMid(mid, 0); // * because slider will not let get bubble by document.elementFromPoint
      } else if(!this.pinnedMessage.isLocked()) {
        this.pinnedMessage.handleFollowingPinnedMessage();
        this.pinnedMessage.testMid(mid);
      }
    });

    this.listenerSetter.add(rootScope)('peer_pinned_messages', ({peerId, mids}) => {
      if(this.chat.type !== ChatType.Pinned || peerId !== this.peerId) {
        return;
      }

      if(mids) {
        this.setTitle();
      }
    });

    this.listenerSetter.add(rootScope)('monoforum_dialogs_update', async({dialogs}) => {
      if(!this.chat.isMonoforum || !this.chat.monoforumThreadId) return;

      const found = dialogs.find(dialog => dialog.parentPeerId === this.chat.peerId && dialog.peerId === this.chat.monoforumThreadId);
      if(!found) return;

      const callback = await (await this.plates.removeFee.setPeerId(this.chat.peerId)).result;
      callback();
    });

    this.listenerSetter.add(rootScope)('auto_delete_period_update', ({peerId, period}) => {
      if(peerId !== this.peerId || this.chat.type !== ChatType.Chat) return;
      this.avatar.setAutoDeletePeriod(period);
    });

    return this;
  }

  public openPinned(byCurrent: boolean) {
    const currentMid = byCurrent ? +this.pinnedMessage.container.dataset.mid : 0;
    this.chat.appImManager.setInnerPeer({
      peerId: this.peerId,
      // the pinned list is per-topic in a forum — without the thread the tab
      // would list every pin of the forum instead of this topic's
      threadId: this.chat.threadId,
      lastMsgId: currentMid || 0,
      type: ChatType.Pinned
    });
  }

  public onJoinClick = async(button: HTMLElement) => {
    const middleware = this.chat.bubbles.getMiddleware();
    button.setAttribute('disabled', 'true');

    joinChat({
      peerId: this.peerId,
      managers: this.managers,
      appImManager: this.chat.appImManager
    }).finally(() => {
      if(!middleware()) {
        return;
      }

      button.removeAttribute('disabled');
    });
  };

  private canClickOnDirectMessagesBtn = true;
  private onDirectMessagesClick = async() => {
    if(!this.canClickOnDirectMessagesBtn || !this.peerId.isAnyChat()) return;
    this.canClickOnDirectMessagesBtn = false;
    pause(200).then(() => this.canClickOnDirectMessagesBtn = true);

    const chat = this.chat.peer;
    if(chat._ !== 'channel') return;

    appImManager.setInnerPeer({
      peerId: chat.linked_monoforum_id.toPeerId(true)
    });
  };

  private onMuteClick = () => {
    showMutePopup(this.peerId);
  };

  private onUnmuteClick = () => {
    this.managers.appMessagesManager.togglePeerMute({peerId: this.peerId, threadId: this.chat.threadId});
  };

  private onResize = () => {
    this.setFloating();
  };

  private onChangeScreen = (from: ScreenSize, to: ScreenSize) => {
    this.onResize();
  };

  public destroy() {
    // this.chat.log.error('Topbar destroying');
    this.listenerSetter.removeAll();

    this.status?.destroy();
    this.titleMiddlewareHelper?.destroy();
    this.avatarMiddlewareHelper?.destroy();
    this.pinnedMessage?.destroy();
    this.plates?.destroy();

    delete this.pinnedMessage;
    delete this.plates;
  }

  public cleanup() {
    if(!this.chat.peerId) {
      this.container.classList.add('hide');
    }
  }

  private appendPinnedMessage(pinnedMessage: ChatPinnedMessageController) {
    const container = pinnedMessage.container;
    if(this.pinnedMessage && this.pinnedMessage !== pinnedMessage) {
      this.pinnedMessage.container.replaceWith(container);
    } else {
      this.floatingPlatesWrapper.prepend(container);
    }
  }

  private pinnedMessageSetupForKey: string | undefined;
  /**
   * Plate swap deferred from `setupPinnedMessageForPeer` until the
   * bubbles-mount moment inside `bubbles.setPeer`. `kind='install'`
   * carries a detached new plate and (optionally) a `prepareInitialPromise`
   * that resolves once the new plate's content is rendered — awaited in
   * `finishPeerChange`'s `Promise.all`, so by the time
   * `revealPreparedPinnedMessage` runs the plate and bubbles paint together. `kind='destroy'` means the new peer has no plate,
   * but the old one must stay visible until bubbles swap.
   */
  private pendingPinnedSetup:
    | {kind: 'install', newPlate: ChatPinnedMessageController, prepareInitialPromise?: Promise<void>}
    | {kind: 'destroy'}
    | null = null;

  /**
   * Prepare the pinned-message plate for the chat's current peer. Called
   * from `finishPeerChange` alongside the other plates (after
   * `onChangePeer` has updated `messagesStorageKey` / `this.type` /
   * etc), so peer-derived lookups inside `prepareInitial` already point
   * at the new peer.
   *
   * Does NOT touch the DOM — the change is staged into
   * `pendingPinnedSetup` and applied atomically by
   * `revealPreparedPinnedMessage` right before bubbles mount, so plate
   * and bubbles paint in the same frame.
   *
   * Idempotent per peer + thread. Returns the `prepareInitial` promise
   * (if any) so callers can await content readiness in parallel with the
   * other `finishPeerChange` promises.
   */
  public setupPinnedMessageForPeer(): Promise<void> | undefined {
    const peerId = this.chat.peerId;
    const threadId = this.chat.threadId;
    // The pinned list is per-topic in a forum, and on mobile the same chat
    // instance is reused across peer changes — keying on peerId alone would
    // keep the previous topic's plate on a topic switch.
    const setupKey = peerId + (threadId ? '_' + threadId : '');
    if(this.pinnedMessageSetupForKey === setupKey) {
      return this.pendingPinnedSetup?.kind === 'install' ?
        this.pendingPinnedSetup.prepareInitialPromise :
        undefined;
    }
    this.pinnedMessageSetupForKey = setupKey;

    // Drop an orphaned pending plate from an even earlier peer change.
    if(this.pendingPinnedSetup?.kind === 'install') {
      this.pendingPinnedSetup.newPlate.destroy();
    }
    this.pendingPinnedSetup = null;

    const isPinnedMessagesNeeded = this.chat.isPinnedMessagesNeeded();
    const wantsPlate = isPinnedMessagesNeeded || this.chat.type === ChatType.Discussion;
    if(!wantsPlate) {
      if(this.pinnedMessage) {
        this.pendingPinnedSetup = {kind: 'destroy'};
      }
      return;
    }

    const newPlate = createChatPinnedMessage(this, this.chat, this.managers);
    let prepareInitialPromise: Promise<void> | undefined;

    if(this.chat.type === ChatType.Discussion) {
      newPlate.setStaticMessage(this.chat.threadId);
    } else {
      newPlate.setUserHidden(!!this.chat.appState.hiddenPinnedMessages[getPinnedMessagesKey(peerId, threadId)]);
      const savedPosition = this.chat.appImManager.getChatSavedPosition(this.chat);
      const cachedFull = untrack(() => this.chat.fullPeer());
      // Prefer the full saved plate state (mid + index + count) so the
      // plate restores exactly the view the user left on — including the
      // pin-list border indicator and counter. Fall back to fullPeer's
      // `pinned_msg_id`, which is always the newest pin (index 0).
      // `pinned_msg_id` is peer-scoped. In a forum topic the pinned list is
      // per-topic (the plate fetches it with `threadId`), so seeding from it
      // would paint a foreign pin — or any pin at all in a topic that has
      // none — until the thread-scoped fetch resolves and hides it again.
      const pinnedMessageId = !threadId && cachedFull && 'pinned_msg_id' in cachedFull ?
        cachedFull.pinned_msg_id :
        undefined;
      const hint = savedPosition?.pinnedMessages ||
        (pinnedMessageId ? {mid: pinnedMessageId, index: 0, count: 1} : undefined);
      if(hint) {
        prepareInitialPromise = newPlate.prepareInitial(hint);
      }
    }

    this.pendingPinnedSetup = {kind: 'install', newPlate, prepareInitialPromise};
    return prepareInitialPromise;
  }

  /**
   * Apply the deferred plate change synchronously. Called from the
   * `finishPeerChange` sync callback, *after* `Promise.all(promises)`
   * already awaited `prepareInitialPromise` — so the new plate's content
   * is in the DOM by now. The install + reveal lands in the same task as
   * the surrounding bubbles work, painting together.
   */
  public revealPreparedPinnedMessage() {
    const pending = this.pendingPinnedSetup;
    if(!pending) {
      this.pinnedMessage?.revealPrepared();
      return;
    }
    this.pendingPinnedSetup = null;

    const old = this.pinnedMessage;
    if(pending.kind === 'install') {
      this.appendPinnedMessage(pending.newPlate);
      this.pinnedMessage = pending.newPlate;
      pending.newPlate.revealPrepared();
    } else {
      this.pinnedMessage = undefined;
    }
    old?.destroy();
    this.setFloating();
  }

  public async finishPeerChange(options: Parameters<Chat['finishPeerChange']>[0]) {
    const {peerId, threadId, monoforumThreadId} = this.chat;
    const {middleware} = options;

    let newAvatar: ChatTopbar['avatar'], newAvatarMiddlewareHelper: ChatTopbar['avatarMiddlewareHelper'];
    const isSaved = this.chat.type === ChatType.Saved;
    const needArrowBack = this.chat.type === ChatType.Search;
    if([ChatType.Chat, ChatType.Static, ChatType.Logs].includes(this.chat.type) || isSaved) {
      const usePeerId = monoforumThreadId ||(isSaved ? threadId : peerId);
      const useThreadId = isSaved ? undefined : threadId;
      const avatar = this.avatar;


      if(
        !avatar ||
        avatar.node.dataset.peerId.toPeerId() !== usePeerId ||
        avatar.node.dataset.threadId !== (useThreadId ? '' + useThreadId : undefined) ||
        peerId === rootScope.myId
      ) {
        newAvatar = avatarNew({
          middleware: (newAvatarMiddlewareHelper = getMiddleware()).get(),
          isDialog: true,
          size: 40,
          peerId: usePeerId,
          threadId: useThreadId,
          wrapOptions: {customEmojiSize: makeMediaSize(30, 30)},
          withStories: true,
          withVideoAvatar: true,
          meAsNotes: isSaved
        });
        newAvatar.node.classList.add('person-avatar');
      } else {
        newAvatar = this.avatar;
      }
    }

    this.status?.destroy();
    const status = this.status = this.createStatus();

    const promises = [
      this.managers.appPeersManager.isBroadcast(peerId),
      this.managers.appPeersManager.isAnyChat(peerId),
      peerId.isAnyChat() ? this.chat.peer : undefined,
      newAvatar?.readyThumbPromise,
      this.setTitleManual(),
      status?.prepare(true),
      modifyAckedPromise(this.plates.requests.setPeerId(peerId)),
      modifyAckedPromise(this.plates.actions.setPeerId(peerId)),
      modifyAckedPromise(this.plates.removeFee.setPeerId(peerId)),
      this.chat.type === ChatType.Chat ? await modifyAckedPromise(this.chat.getAutoDeletePeriod()) : undefined,
      this.setupPinnedMessageForPeer()
    ] as const;

    const [
      isBroadcast,
      isAnyChat,
      chat,
      _,
      setTitleCallback,
      setStatusCallback,
      setRequestsCallback,
      setActionsCallback,
      setChatRemoveFeeCallback,
      autoDeletePeriod,
      _pinnedReady
    ] = await Promise.all(promises);

    if(!middleware() && newAvatarMiddlewareHelper) {
      newAvatarMiddlewareHelper.destroy();
    }

    return () => {
      const canHaveSomeButtons = !(this.chat.type === ChatType.Pinned || this.chat.type === ChatType.Scheduled || this.chat.type === ChatType.Welcome || this.chat.type === ChatType.Static || this.chat.type === ChatType.Logs);
      const canHaveSearch = canHaveSomeButtons || this.chat.type === ChatType.Logs;

      if(this.btnSearch) {
        this.btnSearch.classList.toggle('hide', !canHaveSearch);
      }

      if(this.btnLogFilters) {
        this.btnLogFilters.classList.toggle('hide', this.chat.type !== ChatType.Logs);
      }

      if(this.avatar !== newAvatar) {
        if(newAvatar) {
          this.person.prepend(newAvatar.node);
        }

        if(this.avatar) {
          this.avatarMiddlewareHelper.destroy();
          this.avatar.node.remove();
        }

        this.avatar = newAvatar;
        this.avatarMiddlewareHelper = newAvatarMiddlewareHelper;
        this.container.classList.toggle('has-avatar', !!newAvatar);
      }

      callbackify(autoDeletePeriod?.result, (value) => {
        this.avatar?.setAutoDeletePeriod(value);
      });

      this.verifyButtons();

      const canHaveMore = canHaveSomeButtons || this.chat.type === ChatType.Logs;

      if(this.btnMore) {
        this.btnMore.classList.toggle('hide', !canHaveMore);
      }

      if(this.chat.type === ChatType.Welcome) {
        void this.updateWelcomeMoreButton(true);
      }

      this.revealPreparedPinnedMessage();
      setTitleCallback();
      setStatusCallback?.();

      this.subtitle.classList.toggle('hide', !setStatusCallback);

      this.container.classList.remove('hide');

      if(setRequestsCallback.result instanceof Promise) {
        this.plates.requests.unset(peerId);
      }

      if(setActionsCallback.result instanceof Promise) {
        this.plates.actions.unset(peerId);
      }

      if(setChatRemoveFeeCallback.result instanceof Promise) {
        this.plates.removeFee.hide();
      }

      this.plates.live?.setPeerId(peerId);
      this.plates.groupCall?.setPeerId(peerId);
      this.plates.translation.setPeerId(peerId);
      this.plates.sponsored.setPeerId(peerId);

      callbackify(setRequestsCallback.result, (callback) => {
        if(!middleware()) {
          return;
        }

        callback();
      });

      callbackify(setActionsCallback.result, (callback) => {
        if(!middleware()) {
          return;
        }

        callback();
      });

      callbackify(setChatRemoveFeeCallback.result, (callback) => {
        if(!middleware()) {
          return;
        }

        callback();
      });

      this.container.classList.toggle('show-back-button', needArrowBack);
    };
  }

  public async setTitleManual(count?: number) {
    const {peerId, threadId, monoforumThreadId} = this.chat;
    let titleEl: HTMLElement, icons: Element[];
    const oldMiddlewareHelper = this.titleMiddlewareHelper;
    oldMiddlewareHelper?.destroy();
    const middlewareHelper = this.titleMiddlewareHelper = getMiddleware();
    const middleware = middlewareHelper.get();
    if(this.chat.type === ChatType.Pinned) {
      if(count === undefined) titleEl = i18n('Loading');
      else titleEl = i18n('PinnedMessagesCount', [count]);

      if(count === undefined) {
        // Not `messages.getSearchCounters`: for some peers the server answers it
        // with an `inexact` count that undercounts badly — in Saved Messages it
        // returns 5 while 17 messages are pinned (an exact answer there needs
        // `saved_peer_id`, which the peer-wide pinned tab has no business
        // sending). `getPinnedMessage` reads the count off the very
        // `inputMessagesFilterPinned` search this tab's list is built from, so
        // the title can't disagree with what's rendered under it — the same
        // source the official clients use for this counter.
        this.managers.appMessagesManager.getPinnedMessage(peerId, this.chat.threadId).then(({count}) => {
          if(!middleware()) return;
          this.setTitle(count);

          // ! костыль х2, это нужно делать в другом месте
          if(!count) {
            this.chat.appImManager.setPeer(); // * close tab

            // ! костыль, это скроет закреплённые сообщения сразу, вместо того, чтобы ждать пока анимация перехода закончится
            const originalChat = this.chat.appImManager.chat;
            if(originalChat.topbar.pinnedMessage) {
              originalChat.topbar.pinnedMessage.setHidden(true);
            }
          }
        });
      }
    } else if(this.chat.type === ChatType.Scheduled) {
      titleEl = i18n(peerId === rootScope.myId ? 'Reminders' : 'ScheduledMessages');
    } else if(this.chat.type === ChatType.Welcome) {
      titleEl = i18n('WelcomeMessages.Title');
    } else if(this.chat.type === ChatType.Discussion) {
      const el = this.messagesCounter({
        middleware,
        key: 'Chat.Title.Comments',
        minusFirst: this.chat.isForum
      });
      if(count === undefined && middleware()) {
        const historyStorage = this.chat.getHistoryStorage();
        el.compareAndUpdate(typeof(historyStorage.count) !== 'number' ?
          {key: 'Loading', args: undefined} :
          {args: [historyStorage.count - (this.chat.isForum ? 1 : 0)]}
        );
      }

      titleEl = el.element;
    } else if(this.chat.type === ChatType.Chat || this.chat.type === ChatType.Saved || this.chat.type === ChatType.Static || this.chat.type === ChatType.Logs) {
      const usePeerId = monoforumThreadId || (this.chat.type === ChatType.Saved ? threadId : peerId);

      [titleEl/* , icons */] = await Promise.all([
        wrapPeerTitle({
          peerId: usePeerId,
          dialog: true,
          withIcons: !threadId,
          threadId: threadId,
          wrapOptions: {middleware},
          meAsNotes: this.chat.type === ChatType.Saved
        })
        // generateTitleIcons(peerId)
      ]);
    }

    // Always a callback, never `undefined` — `setPeerCallbacks` and `setTitle`
    // both call the result unconditionally, so bailing out here used to risk a
    // 'setTitleCallback is not a function' in the middle of a peer change.
    return () => {
      // A newer `setTitleManual` destroyed our helper, so it owns the title now.
      // Without this the pinned tab could end up stuck on 'Loading': the count
      // resolves from cache before the caller gets around to invoking this
      // callback, and the stale 'Loading' element would overwrite the count
      // that the inner `setTitle(count)` already painted.
      if(!middleware()) {
        return;
      }

      replaceContent(this.title, titleEl);
      // if(icons) {
      //   this.title.append(...icons);
      // }
    };
  }

  public setTitle(count?: number) {
    this.setTitleManual(count).then((setTitleCallback) => setTitleCallback());
  }

  public setFloating = () => {
    // Preview omits `floatingPlatesWrapper` from the live DOM (see `construct`). The plate
    // controllers still exist for the rest of the code, and a peer with a pinned message
    // would mark the pinnedMessage plate visible — its hardcoded `height: 48` + TOPBAR_GAP
    // would leak into `--pinned-floating-height` / `pinnedFloatingHeightPx` and inflate
    // `bubbles-padding-top` from 56 to 112 above the first message. Bail early.
    if(this.chat.isPreview) return;
    const containers = [
      this.pinnedMessage,
      ...(this.plates?.all || [])
    ].filter(Boolean);
    const TOPBAR_GAP = 8;
    const PLATE_DIVIDER = 1;

    const visible = containers.filter((container) => container.isVisible());
    const count = visible.length;

    // Un-hide the wrapper BEFORE measuring any 'auto' plate height below. The
    // wrapper is `display: none` while no plate is visible (count === 0), and an
    // ancestor's `display: none` zeroes its descendants' offsetHeight — so the
    // sponsored plate (the only `height: 'auto'` one), exactly when it is the
    // plate that flips the count 0 → 1, would otherwise be measured at 0 and
    // never reserve its padding-top, leaving it overlapping the chat content.
    this.floatingPlatesWrapper.classList.toggle('hide', count === 0);

    let platesHeight = 0;
    for(const container of visible) {
      platesHeight += container.height === 'auto' ? container.container.offsetHeight : container.height;
    }

    const floatingHeight = count > 0 ? platesHeight + Math.max(0, count - 1) * PLATE_DIVIDER + TOPBAR_GAP : 0;
    const reservedFloatingHeight = this.chat.container.classList.contains('is-search-active') ? 0 : floatingHeight;
    this.container.dataset.floating = '' + count;
    this.chat.container.style.setProperty(
      '--pinned-floating-height',
      `calc(${reservedFloatingHeight}px + var(--topbar-floating-call-height) + var(--topbar-floating-audio-height))`
    );
    this.chat.updatePinnedFloatingHeight(reservedFloatingHeight);
  };

  private messagesCounter({
    middleware,
    key,
    minusFirst,
    displayLoading
  }: {
    middleware: Middleware,
    key: LangPackKey,
    minusFirst?: boolean,
    displayLoading?: boolean
  }) {
    const el = new I18n.IntlElement(displayLoading ? {
      key: 'Loading'
    } : {
      key,
      args: [1]
    });

    createRoot((dispose) => {
      middleware.onDestroy(dispose);

      createEffect(on(
        () => [this.chat.historyStorage.count, this.chat.historyStorage.wasFetched] as const,
        ([count]) => {
          el.compareAndUpdate({key, args: [count - (minusFirst ? 1 : 0)]});
        },
        {defer: displayLoading ? !this.chat.historyStorage.wasFetched : true}
      ));
    });

    return el;
  }

  private createStatus() {
    if(!this.subtitle || (this.chat.type !== ChatType.Chat && this.chat.type !== ChatType.Saved)) return;
    const middlewareHelper = getMiddleware();
    const middleware = middlewareHelper.get();
    const listenerSetter = new ListenerSetter();

    let prepare: (needClear: boolean) => Promise<() => void>;
    if(this.chat.type === ChatType.Saved || this.peerId === rootScope.myId) {
      const el = this.messagesCounter({
        middleware,
        key: 'messages',
        displayLoading: this.peerId === rootScope.myId
      });

      prepare = async() => {
        const historyStorage = this.chat.getHistoryStorage();
        el.compareAndUpdate({args: [historyStorage.count]});
        return () => replaceContent(this.subtitle, el.element);
      };
    } else if(this.chat.threadId) {
      prepare = async() => {
        const title = await wrapPeerTitle({peerId: this.peerId, dialog: true});
        const span = i18n('TopicProfileStatus', [title]);

        return () => replaceContent(this.subtitle, span);
      };
    } else if(this.chat.isMonoforum && this.chat.canManageDirectMessages && !this.chat.monoforumThreadId) {
      listenerSetter.add(rootScope)('monoforum_dialogs_update', ({dialogs}) => {
        if(!dialogs.find(dialog => dialog.parentPeerId === this.chat.peerId)) return;
        setAuto();
      });

      listenerSetter.add(rootScope)('monoforum_dialogs_drop', () => {
        setAuto();
      });

      prepare = async() => {
        const ackedResult = await this.managers.acknowledged.monoforumDialogsStorage.getDialogs({parentPeerId: this.peerId, limit: 1});
        const initialCount = ackedResult.cached ? (await ackedResult.result).count || 0 : '~';

        const el = new I18n.IntlElement({
          key: 'ChannelDirectMessages.ThreadsCount',
          args: [initialCount]
        });

        if(!ackedResult.cached) ackedResult.result?.then(({count}) => el.compareAndUpdate({
          key: 'ChannelDirectMessages.ThreadsCount',
          args: [count]
        }));

        return () => replaceContent(this.subtitle, el.element);
      };
    } else if(this.chat.isMonoforum) {
      listenerSetter.add(rootScope)('history_multiappend', (message) => {
        if(message.peerId !== this.chat.peerId) return;
        if(this.chat.monoforumThreadId && getPeerId(message?.saved_peer_id) !== this.chat.monoforumThreadId) return;
        setAuto();
      });

      listenerSetter.add(rootScope)('history_delete', ({peerId}) => {
        if(peerId !== this.chat.peerId) return;
        setAuto();
      });

      prepare = async() => {
        const ackedResult = await this.managers.acknowledged.appMessagesManager.getHistory({
          ...this.chat.requestHistoryOptionsPart,
          fetchIfWasNotFetched: true,
          limit: 1
        });

        const initialCount = ackedResult.cached ? (await ackedResult.result).count || 0 : '~';

        const el = new I18n.IntlElement({
          key: 'ChannelDirectMessages.MessagesCount',
          args: [initialCount]
        });

        if(!ackedResult.cached) ackedResult.result?.then(({count}) => el.compareAndUpdate({
          key: 'ChannelDirectMessages.MessagesCount',
          args: [count]
        }));

        return () => replaceContent(this.subtitle, el.element);
      };
    } else {
      const peerId = this.peerId;

      listenerSetter.add(rootScope)('peer_typings', ({peerId: _peerId}) => {
        if(peerId === _peerId) {
          setAuto();
        }
      });

      listenerSetter.add(rootScope)('user_update', (userId) => {
        if(peerId === userId.toPeerId()) {
          setAuto();
        }
      });

      const interval = window.setInterval(() => setAuto(), 60e3);

      middleware.onDestroy(() => {
        clearInterval(interval);
      });

      prepare = (needClear) => {
        return this.chat.appImManager.setPeerStatus({
          peerId,
          element: this.subtitle,
          needClear,
          useWhitespace: false,
          middleware
        });
      };
    }

    middleware.onDestroy(() => {
      listenerSetter.removeAll();
    });

    const setAuto = () => {
      prepare(false).then((callback) => middleware() && callback?.());
    };

    return {
      prepare,
      destroy: () => middlewareHelper.destroy()
    };
  }

  private async createAutoDeleteSubmenu() {
    const options = getDefaultOptions({
      offLabel: () => i18n('Never')
    });

    const menu = await ButtonMenu({
      buttons: [
        ...options.map((option): ButtonMenuItemOptionsVerifiable => ({
          iconElement: option.value === 0 ? Icon('auto_delete_circle_off') : createAutoDeleteIcon(option.value),
          regularText: option.label(),
          onClick: () => {
            this.managers.appPrivacyManager.setAutoDeletePeriodFor(this.peerId, option.value);
          }
        })),
        {
          icon: 'tools',
          text: 'Other',
          onClick: () => this.chat.openAutoDeleteMessagesCustomTimePopup()
        }
      ]
    });

    return menu;
  }
}
